"use client";

import { ImmersiveVimeoPlayer } from "@/components/immersive-vimeo-player";
import {
  detectVideoProvider,
  extractVimeoId,
  extractYouTubeId,
  buildYouTubeEmbedUrl,
} from "@/lib/video-utils";
import { buildImmersiveVimeoEmbedUrl } from "@/lib/vimeo";

type VideoPlayerProps = {
  url: string;
  title?: string | null;
  className?: string;
  immersive?: boolean;
};

export function VideoPlayer({
  url,
  title,
  className = "",
  immersive = false,
}: VideoPlayerProps) {
  const provider = detectVideoProvider(url);

  if (provider === "vimeo") {
    const vimeoId = extractVimeoId(url);

    if (!vimeoId) {
      return null;
    }

    if (immersive) {
      return (
        <ImmersiveVimeoPlayer
          title={title ?? "Video"}
          embedUrl={buildImmersiveVimeoEmbedUrl(vimeoId)}
          className={className}
        />
      );
    }

    return (
      <iframe
        src={`https://player.vimeo.com/video/${vimeoId}?rel=0`}
        title={title ?? "Video"}
        className={`block h-full w-full border-0 ${className}`}
        loading="lazy"
        allow="autoplay; fullscreen; picture-in-picture"
        allowFullScreen
      />
    );
  }

  if (provider === "youtube") {
    const youtubeId = extractYouTubeId(url);

    if (!youtubeId) {
      return null;
    }

    return (
      <iframe
        src={buildYouTubeEmbedUrl(youtubeId)}
        title={title ?? "Video"}
        className={`block h-full w-full border-0 ${className}`}
        loading="lazy"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    );
  }

  return null;
}
